/**
 * Probefahrt Map Banner — JavaScript
 * Handles: Intersection Observer (reveal), Parallax, Staggered Pins
 */

class ProbefahrtMapBanner {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.bgImage = wrapper.querySelector('.pmb-bg-image');
    this.pins = Array.from(wrapper.querySelectorAll('.pmb-pin'));

    this.useParallax  = wrapper.dataset.parallax  === 'true';
    this.useStagger   = wrapper.dataset.stagger    === 'true';
    this.useZoom      = wrapper.dataset.zoom       === 'true';

    this.revealed = false;

    this._onScroll    = this._onScroll.bind(this);
    this._onIntersect = this._onIntersect.bind(this);

    this._init();
  }

  _init() {
    // Intersection Observer triggers the "visible" state
    const observer = new IntersectionObserver(this._onIntersect, {
      threshold: 0.15,
    });
    observer.observe(this.wrapper);

    // Parallax: only register scroll listener if needed & non-reduced-motion
    if (this.useParallax && !this._prefersReducedMotion()) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    // iFrame interaction: hide content when user clicks inside the Atlist map
    const iframe = this.wrapper.querySelector('.pmb-iframe');
    if (iframe) {
      this._setupIframeInteraction(iframe);
    }
  }

  _setupIframeInteraction(iframe) {
    const inner = this.wrapper.querySelector('.pmb-content__inner');
    const overlay = this.wrapper.querySelector('.pmb-touch-overlay');
    const mapOverlay = this.wrapper.querySelector('.pmb-overlay');
    if (!inner) return;

    let fadeTimer = null;
    const isTouchDevice = () => window.matchMedia('(hover: none)').matches;

    const hide = (immediate = false) => {
      clearTimeout(fadeTimer);
      if (immediate) {
        inner.classList.add('pmb-content--hidden');
        mapOverlay?.classList.add('pmb-overlay--hidden');
      } else {
        fadeTimer = setTimeout(() => {
          inner.classList.add('pmb-content--hidden');
          mapOverlay?.classList.add('pmb-overlay--hidden');
        }, 1800);
      }
    };

    const show = () => {
      clearTimeout(fadeTimer);
      inner.classList.remove('pmb-content--hidden');
      mapOverlay?.classList.remove('pmb-overlay--hidden');
    };

    const enableOverlay = () => {
      overlay?.classList.remove('pmb-touch-overlay--pass');
    };

    const passOverlay = () => {
      overlay?.classList.add('pmb-touch-overlay--pass');
    };

    // ── Desktop: mouseenter / mouseleave ───────────────────
    iframe.addEventListener('mouseenter', () => {
      if (isTouchDevice()) return;
      hide(false);
    });

    iframe.addEventListener('mouseleave', () => {
      if (isTouchDevice()) return;
      show();
    });

    // ── Mobile: touch overlay intercepts first tap ──────────
    // The overlay sits above the iframe and propagates touchstart
    // to the parent page (unlike cross-origin iframe touch events).
    if (overlay) {
      overlay.addEventListener('touchstart', () => {
        hide(true);         // hide text immediately
        passOverlay();      // let iframe receive subsequent touches
      }, { passive: true });

      // Tap anywhere outside the map → restore text + re-enable overlay
      document.addEventListener('touchstart', (e) => {
        const inMap = iframe.contains(e.target) || e.target === iframe || e.target === overlay;
        if (!inMap) {
          show();
          enableOverlay();  // ready for next tap on the map
        }
      }, { passive: true });
    }

    // ── Desktop fallback: window blur on pin click ──────────
    window.addEventListener('blur', () => {
      if (!isTouchDevice()) hide(true);
    });

    window.addEventListener('focus', () => {
      if (!isTouchDevice()) show();
    });
  }

  _onIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.revealed) {
        this.revealed = true;
        this.wrapper.classList.add('pmb--visible');

        if (this.useStagger && !this._prefersReducedMotion()) {
          this._revealPinsStaggered();
        } else {
          // Make all pins instantly visible if stagger is off
          this.pins.forEach(pin => pin.classList.add('pmb-pin--revealed'));
        }
      }
    });
  }

  _revealPinsStaggered() {
    this.pins.forEach((pin, i) => {
      setTimeout(() => {
        pin.classList.add('pmb-pin--revealed');
      }, 120 + i * 90);
    });
  }

  _onScroll() {
    if (!this.bgImage) return;
    const rect = this.wrapper.getBoundingClientRect();
    const viewH = window.innerHeight;

    // Only parallax when element is in viewport
    if (rect.bottom < 0 || rect.top > viewH) return;

    // Parallax offset: image moves at 30% of scroll speed
    const progress  = (viewH - rect.top) / (viewH + rect.height);
    const maxOffset = 40; // px, tune here
    const offset    = (progress - 0.5) * maxOffset * -1;

    // Combine with zoom scale if needed
    const scale = (this.useZoom && this.revealed) ? 1 : 1.06;
    this.bgImage.style.transform = `scale(${scale}) translateY(${offset}px)`;
  }

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

// ── Bootstrap ──────────────────────────────────────────────
function initProbefahrtBanners() {
  document.querySelectorAll('.pmb-wrapper').forEach(wrapper => {
    new ProbefahrtMapBanner(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProbefahrtBanners);
} else {
  initProbefahrtBanners();
}

// Shopify Theme Editor: re-init on section load/reselect
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.pmb-wrapper').forEach(wrapper => {
    new ProbefahrtMapBanner(wrapper);
  });
});
